import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { reduceEmbedCode } from "@webng/web-embed-preprocessors";
import { OEmbed } from "../webembed/OEmbed";
import { LinkListItem } from "./LinkListItem";
import { UpdateOEmbedResult, updateOEmbed } from "../content/WebEmbedContentBlockView";
import { useAuthenticatedApi } from "@webng/react-app-common";
import { isRelativeUrl } from "../utils/regex";
import { useRelativeLinkBaseUrl } from "../LiveblogRenderContext";
import { ILinkListAd } from "@webng-types/write-model";


export function LinkListAdItem({link, oembed} : {link :string, oembed: OEmbed|undefined}) {
    const [json, setJson] = useState<OEmbed|undefined>(oembed)
    const apiClient = useAuthenticatedApi();
    const relativeLinkBaseUrl = useRelativeLinkBaseUrl();
  
    const baseUrl = relativeLinkBaseUrl || ((typeof window !== "undefined") && window.location && window.location.origin);
    const url = baseUrl && isRelativeUrl(link) ? new URL(link, baseUrl).toString() : link

    // updateOEmbed reduces embed codes, so a resolved result carries _original = the
    // reduced URL. This read-only item cannot rewrite its stored link — compare
    // staleness against the effective (reduced) input, or an entry stored as a raw
    // embed code would refetch forever (resolve-long-embed-codes, design D6/F9 —
    // same convergence rule as WebEmbedContentBlockView).
    const effectiveUrl = useMemo(() => reduceEmbedCode(url) ?? url, [url])

    const onOEmbedUpdated = useCallback((result: UpdateOEmbedResult) => {
      if(url === result.requestedUrl) {
        if(result.oembed) {
          setJson(result.oembed)
        }
      }
    }, [url, setJson])

    useEffect(() => {
      // current when _original matches the effective (reduced) input OR the stored
      // raw input — same legacy-entry rule as WebEmbedContentBlockView (Codex P2).
      const isCurrent = json && (json._original === effectiveUrl || json._original === url)
      if(!isCurrent) {
        const promise = updateOEmbed(apiClient, url, onOEmbedUpdated)
        return () => promise.cancel()
      }
    }, [json, apiClient, url, effectiveUrl, onOEmbedUpdated])
  
    if(json) {
      return <LinkListItem oembed={json} />
    } else {
      return <React.Fragment />
    }
  }

 export function LinkListAd({linkListAd}: { linkListAd: ILinkListAd }) {
    const {t} = useTranslation("liveblog");
    
    return <div className="tik4-link-list">
      <div className={`tik4-link-list__header ${linkListAd.links.length === 1 ? "tik4-link-list__header--one" : ""}`} role="heading" aria-level={3}>
        <span className="tik4-link-list__header-content">{linkListAd.title || t("liveblog.link_list.title", { count: linkListAd.links.length })}</span>
      </div>
      <div className="tik4-link-list__list" role="list">
        {linkListAd.links.map((link, i) => {
          try {
            const oembed = JSON.parse(link) as OEmbed;
            return <div className="tik4-link-list__list-item" role="listitem" key={i}><LinkListAdItem link={oembed._original} oembed={oembed} /></div>
          } catch (e) {
            return <div className="tik4-link-list__list-item" role="listitem" key={i}><LinkListAdItem link={link} oembed={undefined} /></div>
          }
        })}
      </div>
    </div>
  }